// src/components/dashboard components/forms/ContactUsHeroForm.jsx
import React, { useEffect, useState } from "react";
import { Plus, Save, Pencil, X, Upload } from "lucide-react";

// ✅ Environment variable base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContactUsHeroForm = ({
  apiBase = `${API_BASE_URL}/contact`,
}) => {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    image: "",
  });

  // =============================
  // FETCH LATEST CONTACT HERO
  // =============================
  const fetchContactHero = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      console.log("🔍 Fetching from:", `${apiBase}/admin/hero`);

      const res = await fetch(`${apiBase}/admin/hero`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      console.log("📡 Response status:", res);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ API Error:", errorText);
        throw new Error(`Failed to fetch: ${res.status}`);
      }

      const data = await res.json();
      console.log("✅ Received data:", data);

      // Handle both array and single object responses
      let arr = [];
      if (Array.isArray(data)) {
        arr = data;
      } else if (data && Object.keys(data).length > 0) {
        arr = [data];
      }
      
      console.log("📦 Processed rows:", arr);
      setRows(arr);
    } catch (err) {
      console.error("❌ Error fetching contact hero:", err);
      alert(`Error loading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactHero();
  }, []);

  // =============================
  // RESET FORM
  // =============================
  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      description: "",
      image: "",
    });
    setEditingId(null);
  };

  // =============================
  // HANDLE INPUT CHANGE
  // =============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =============================
  // HANDLE IMAGE UPLOAD
  // =============================
const handleImageUpload = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const validTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!validTypes.includes(file.type)) {
    alert("Please upload a valid image (JPG, PNG, WEBP).");
    return;
  }

  if (file.size > 2 * 1024 * 1024) {
    alert("Image size must be under 2MB.");
    return;
  }

  // ✅ Upload to S3 via your API
  const formDataUpload = new FormData();
  formDataUpload.append("media", file);

  fetch(`${API_BASE_URL}/admin/upload-media`, {
    method: "POST",
    body: formDataUpload,
  })
    .then((res) => res.json())
    .then((data) => {
      const uploadedUrl = data.data.files?.[0]?.url;
      if (uploadedUrl) {
        setFormData((prev) => ({
          ...prev,
          image: uploadedUrl,
        }));
      }
    })
    .catch((err) => {
      console.error("upload-media error", err);
      alert("Failed to upload image. Please try again.");
    });
};

  const handleRemoveImage = () =>
    setFormData((prev) => ({ ...prev, image: "" }));

  // =============================
  // EDIT EXISTING
  // =============================
  const handleEdit = (row) => {
    setEditingId(row.contact_hero_id || null);
    setFormData({
      title: row.title || "",
      subtitle: row.subtitle || "",
      description: row.description || "",
      image: row.image || "",
    });
    setFormOpen(true);
  };

  // =============================
  // SUBMIT (CREATE / UPDATE)
  // =============================
  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const payload = {
      contact_hero_id: editingId || 0,
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim(),
      description: formData.description.trim(),
      image:
        typeof formData.image === "string" ? formData.image.trim() : "",
    };

    if (!payload.title) {
      alert("Title is required.");
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(`${apiBase}/admin/hero/save`, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save contact hero.");

      await fetchContactHero();
      resetForm();
      setFormOpen(false);
    } catch (err) {
      console.error("Error saving contact hero:", err);
      alert("Failed to save contact hero. Check console.");
    }
  };

  // =============================
  // RENDER
  // =============================
  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Contact Us Hero
        </h2>
        <button
          onClick={() => {
            if (formOpen) resetForm();
            setFormOpen((p) => !p);
          }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          <Plus size={18} />
          {formOpen ? "Close" : "Add / Edit"}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-200 rounded-lg text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-6 py-3 text-left font-semibold">Title</th>
              <th className="px-6 py-3 text-left font-semibold">Subtitle</th>
              <th className="px-6 py-3 text-left font-semibold">Preview</th>
              <th className="px-6 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-5 text-gray-500 italic"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row, index) => (
                <tr
                  key={row.contact_hero_id || `contact-hero-${index}`}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="px-6 py-3">{row.title}</td>
                  <td className="px-6 py-3">{row.subtitle}</td>
                  <td className="px-6 py-3">
                    {row.image ? (
                      <img
                        src={row.image} // ✅ Just use the image URL directly since it's already a full S3 URL
                        alt="contact hero"
                        className="h-12 w-20 object-cover rounded"
                      />
                    ) : (
                      <span className="text-gray-400 italic">No image</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleEdit(row)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <Pencil size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-5 text-gray-500 italic"
                >
                  No contact hero content yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Form */}
      {formOpen && (
        <form onSubmit={handleSubmit} className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold mb-4 text-green-800">
            {editingId ? "Edit Contact Hero" : "Add Contact Hero"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Subtitle
              </label>
              <input
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none resize-y"
              />
            </div>

            {/* Image Upload */}
            <div className="md:col-span-2">
  <label className="block text-sm font-medium mb-1">
    Hero Image
  </label>
  {formData.image ? (
    <div className="relative inline-block">
      <img
        src={formData.image}
        alt="Hero preview"
        className="w-full max-w-md h-64 object-cover rounded-lg border"
      />
      <button
        type="button"
        onClick={handleRemoveImage}
        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
      >
        <X size={18} />
      </button>
    </div>
  ) : (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
      <input
        type="file"
        id="contact-hero-image-upload"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      <label
        htmlFor="contact-hero-image-upload"
        className="cursor-pointer flex flex-col items-center gap-3"
      >
        <Upload size={40} className="text-gray-400" />
        <span className="text-gray-600">Click to upload an Image</span>
        <span className="text-xs text-gray-400">
          Recommended image size: <strong>1600 × 1200 px</strong> (4:3), JPG/PNG, under 400 KB
        </span>
      </label>
    </div>
  )}
</div>

          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg"
            >
              <Save size={18} />
              {editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default ContactUsHeroForm;